import type { ApiLocale } from '@noor/api';

import type { ListResult } from '../model/common';
import {
  imageMutationResponseSchema,
  imageSelector,
  upsertImageItemResultSchema,
  type ImageEntityType,
  type ImageMutationResult,
  type ImageType,
  type UpsertImageItem,
  type UpsertImageItemResult,
} from '../model/image';
import { deleteOneParsed, putList } from './http';

/**
 * Image Service — every entity's photo writes, on one pair of endpoints.
 *
 * See `model/image.ts` for what replaced what and why the old per-entity
 * routes are gone rather than deprecated.
 */
export const imageApi = {
  /**
   * Upsert up to `UPSERT_IMAGES_MAX_BATCH` photos in ONE call —
   * `PUT /merchant/images/{entity_type}/{parent_id}`.
   *
   * Batching is the point: a restaurant's icon + background, a banner's three
   * locales, or a product's cover + gallery all land in a single request
   * instead of a fan-out that could half-fail.
   *
   * This is an UPSERT, not a replace: an item without an `image_id` adds a
   * photo and one with it updates that photo in place, so photos the caller
   * does not mention SURVIVE. Removing one is an explicit {@link remove}.
   *
   * The returned rows describe what was actually written and may disagree with
   * what was asked for — see {@link upsertImageItemResultSchema}.
   */
  upsert(
    entityType: ImageEntityType,
    parentId: string,
    items: UpsertImageItem[],
  ): Promise<ListResult<UpsertImageItemResult>> {
    return putList(
      `merchant/images/${entityType}/${parentId}`,
      { items },
      upsertImageItemResultSchema,
    );
  },

  /**
   * Remove ONE photo — `DELETE /merchant/images/{entity_type}/{parent_id}/{selector}`.
   *
   * `selector` names the slot: an {@link ImageType} for a product or
   * restaurant (passed in the PUT body's uppercase spelling and lowercased
   * here), or a banner's {@link ApiLocale}. `imageId` narrows it to a specific
   * photo and is meaningful ONLY for a product's `common` slot, which is the
   * one case where a selector can match several photos; it rides as a query
   * param rather than in the path.
   *
   * Answers with a receipt, not void: a gated delete removes nothing until a
   * moderator approves, and the caller has to be able to tell the two apart.
   */
  remove(
    entityType: ImageEntityType,
    parentId: string,
    selector: ImageType | ApiLocale,
    imageId?: string,
  ): Promise<ImageMutationResult> {
    return deleteOneParsed(
      `merchant/images/${entityType}/${parentId}/${imageSelector(selector)}`,
      imageMutationResponseSchema,
      imageId === undefined ? undefined : { image_id: imageId },
    );
  },
};
